import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UiIcon from 'ui-components/ui-icon/UiIcon';
import { Tabs, Tab, ButtonBase, MenuItem, useMediaQuery } from '@mui/material';
import UiSkelton from 'ui-components/ui-skelton/UiSkelton';
import { magicNumbers } from 'utils';
import { isFunction, isNumber } from 'lib';
import { UiThemeProvider } from 'themes';
import { StyledUiTabs, StyledUiTabsOverflowMenu } from './UiTabs.styles';
function renderTab(tab, index, selectedTabIndex, dataTestId) {
  if (!tab?.permission) return null;
  const isSelectedTab = index === selectedTabIndex;

  const renderIcon = () => {
    return (
      <UiIcon
        className='icon'
        color={isSelectedTab ? tab.activeColor : null}
        hover={isSelectedTab ? tab?.activeColor : null}
        icon={
          tab?.customeIcon || tab?.customeActiveIcon ? undefined : tab?.icon
        }
        src={isSelectedTab ? tab?.customeActiveIcon : tab?.customeIcon}
        size={20}
      />
    );
  };

  return (
    <Tab
      key={tab.title}
      value={index}
      label={tab.title}
      disabled={tab?.disabled}
      component={
        tab.custom
          ? (props) => (
              <ButtonBase {...props} className='custom-tab'>
                {(tab?.icon || tab?.customeIcon) && renderIcon()}
                <span className='title'>
                  {tab.title}
                  {tab.subTitle && !tab.loading && (
                    <span className='sub-title'>{tab.subTitle}</span>
                  )}
                  {tab.subTitle && tab.loading && (
                    <UiSkelton variant='rectangular' height={20} width={25} />
                  )}
                </span>
              </ButtonBase>
            )
          : undefined
      }
      icon={(tab?.icon || tab?.customeIcon) && renderIcon()}
      iconPosition='start'
      data-testid={`${dataTestId}-tab-${tab.key}`}
    />
  );
}

/**
 * Dropdown of tabs, used in two places: the desktop overflow menu for tabs past
 * maxVisibleTabs, and the mobile layout where the whole bar collapses into a
 * single select. Callers supply the trigger content; the chevron is shared.
 */
function UiTabsMenu({
  tabs,
  children,
  onSelect,
  selectedIndex,
  triggerClassName,
  matchTriggerWidth = false,
  dataTestId,
  testIdPrefix,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (index) => {
    onSelect(index);
    handleClose();
  };

  return (
    <>
      <ButtonBase
        className={triggerClassName}
        onClick={handleOpen}
        aria-haspopup='true'
        aria-expanded={isOpen ? 'true' : undefined}
        data-testid={`${dataTestId}-${testIdPrefix}-trigger`}
      >
        {children}
        <UiIcon
          icon={isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}
          size={20}
          color='#3C607A'
          hover='#3C607A'
        />
      </ButtonBase>
      <StyledUiTabsOverflowMenu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{
          'data-testid': `${dataTestId}-${testIdPrefix}-list`,
        }}
        // The mobile select drops open at exactly the trigger's width
        PaperProps={
          matchTriggerWidth && anchorEl
            ? { style: { width: anchorEl.offsetWidth } }
            : undefined
        }
      >
        {tabs.map(({ tab, index }) => (
          <MenuItem
            key={tab.key || tab.title}
            selected={index === selectedIndex}
            onClick={() => handleSelect(index)}
            disabled={tab?.disabled}
            data-testid={`${dataTestId}-${testIdPrefix}-item-${tab.key}`}
          >
            <span className='overflow-item-title'>
              {/* A tab in the dropdown is never the active one (it gets
                  promoted into the bar instead), so the non-active custom
                  icon variant is always the right one here. */}
              {(tab?.icon || tab?.customeIcon) && (
                <UiIcon
                  className='icon'
                  icon={tab?.icon}
                  src={tab?.customeIcon}
                  size={20}
                />
              )}
              {tab.title}
            </span>
            {tab.subTitle && <span className='sub-title'>{tab.subTitle}</span>}
          </MenuItem>
        ))}
      </StyledUiTabsOverflowMenu>
    </>
  );
}

UiTabsMenu.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      tab: PropTypes.object,
      index: PropTypes.number,
    })
  ).isRequired,
  children: PropTypes.node,
  onSelect: PropTypes.func.isRequired,
  selectedIndex: PropTypes.number,
  triggerClassName: PropTypes.string,
  matchTriggerWidth: PropTypes.bool,
  dataTestId: PropTypes.string,
  testIdPrefix: PropTypes.string,
};

/**
 * UiTabs Component
 *
 * Material ui version 5 does'nt support rtl, because of this instead of using another package
 * tabs reversed and default active tab index set to last item by default that is first item in rtl mode.
 *
 * @component
 *
 */
function UiTabs(props) {
  const {
    tabs,
    defaultSelectedTabKey,
    paramKey,
    onChange,
    actions = null,
    className = '',
    contentClassName = '',
    disableUrlSync = false,
    maxVisibleTabs,
    moreTabsLabel,
    collapseOnMobile = false,
    disableScroll = false,
    ...otherProps
  } = props;

  const { t } = useTranslation();
  const { [paramKey]: currentParam } = useParams();
  const navigate = useNavigate();
  const isMobileWidth = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isCollapsed = collapseOnMobile && isMobileWidth;

  const permissionedTabs = useMemo(
    () => tabs.filter((item) => item?.permission).reverse(),
    [tabs]
  );

  /**
   * When maxVisibleTabs is passed and the tab count exceeds it, tabs are split
   * into a visible group and an overflow group. The original permissionedTabs
   * order and indices stay untouched so tab selection, URL sync, and the
   * component's default behavior are unaffected.
   */
  const indexedPermissionedTabs = useMemo(
    () => permissionedTabs.map((tab, index) => ({ tab, index })),
    [permissionedTabs]
  );

  /**
   * maxVisibleTabs is applied exactly as given: every tab past it goes to the
   * menu, whether that is a single tab or, with 0, all of them.
   */
  const hasOverflow =
    !isCollapsed &&
    isNumber(maxVisibleTabs) &&
    indexedPermissionedTabs.length > maxVisibleTabs;

  const getDefaultSelectedIndex = (tabKey) => {
    if (!tabKey) {
      return permissionedTabs.length - 1;
    }
    const defaultIndex = permissionedTabs.findIndex(
      (tab) => tab.key === defaultSelectedTabKey
    );
    return defaultIndex === magicNumbers.INDEX_NOT_FOUND
      ? permissionedTabs.length - 1
      : defaultIndex;
  };

  const urlIndex = permissionedTabs.findIndex(
    (tab) => tab.key === currentParam
  );

  const [selectedTabIndex, setSelectedTabIndex] = useState(
    getDefaultSelectedIndex(defaultSelectedTabKey)
  );

  useEffect(() => {
    if (!currentParam) {
      setSelectedTabIndex(getDefaultSelectedIndex(defaultSelectedTabKey));
    }
  }, [permissionedTabs]);

  useEffect(() => {
    if (urlIndex > magicNumbers.INDEX_NOT_FOUND) {
      setSelectedTabIndex(urlIndex);
    }
  }, [currentParam, urlIndex]);

  const baseVisibleIndexedTabs = hasOverflow
    ? indexedPermissionedTabs.slice(
        indexedPermissionedTabs.length - maxVisibleTabs
      )
    : indexedPermissionedTabs;

  /**
   * A tab picked from the overflow menu is promoted into the bar as an extra
   * item next to the always-visible ones, so the selection stays on screen.
   * Only the current selection is promoted, so picking another overflow tab
   * replaces the promoted one instead of growing the bar.
   */
  const promotedIndexedTab =
    hasOverflow &&
    !baseVisibleIndexedTabs.some(({ index }) => index === selectedTabIndex)
      ? indexedPermissionedTabs.find(({ index }) => index === selectedTabIndex)
      : undefined;

  const visibleIndexedTabs = promotedIndexedTab
    ? [promotedIndexedTab, ...baseVisibleIndexedTabs]
    : baseVisibleIndexedTabs;

  const overflowIndexedTabs = hasOverflow
    ? indexedPermissionedTabs
        .slice(0, indexedPermissionedTabs.length - maxVisibleTabs)
        .filter(({ index }) => index !== promotedIndexedTab?.index)
        .reverse()
    : [];

  /**
   * permissionedTabs is reversed for the LTR-forced tab bar, so any vertical
   * list has to be flipped back to read top-down in the authored order.
   */
  const listedIndexedTabs = [...indexedPermissionedTabs].reverse();

  /**
   * کلیک روی تب ها
   * @param {*} index
   */
  const handleTabChange = useCallback(
    (_, index) => {
      setSelectedTabIndex(index);
      const selectedTabKey = permissionedTabs[index]?.key;
      if (isFunction(onChange)) {
        onChange(selectedTabKey);
      }
      if (disableUrlSync) {
        return;
      }
      if (!paramKey) {
        window.location.hash = `#${selectedTabKey}`;
        return;
      }
      if (!currentParam) {
        navigate(selectedTabKey);
        return;
      }
      navigate(`../${selectedTabKey}`, {
        relative: 'path',
      });
    },
    [
      currentParam,
      navigate,
      onChange,
      paramKey,
      permissionedTabs,
      disableUrlSync,
    ]
  );

  /**
   * disableScroll only makes sense paired with an active overflow menu — it
   * exists so the bar doesn't scroll AND spill into a dropdown at the same
   * time. Without real overflow (e.g. every platform is configured visible)
   * there is no dropdown to fall back to, so the bar must revert to the
   * original scrollable behavior with its own arrow buttons, or tabs that
   * don't fit the screen would be unreachable.
   */
  const shouldDisableScroll = disableScroll && hasOverflow;

  const tabsElement = (
    <UiThemeProvider theme={{ direction: 'ltr' }}>
      <Tabs
        onChange={handleTabChange}
        value={selectedTabIndex}
        variant={shouldDisableScroll ? 'standard' : 'scrollable'}
        scrollButtons={!shouldDisableScroll}
        {...otherProps}
      >
        {visibleIndexedTabs.map(({ tab, index }) =>
          renderTab(tab, index, selectedTabIndex, props?.['data-testid'])
        )}
      </Tabs>
    </UiThemeProvider>
  );

  const selectedTab = permissionedTabs[selectedTabIndex];

  /**
   * On mobile the bar has no room to scroll, so it collapses into one select
   * that shows the active tab and lists every tab in its dropdown.
   */
  const collapsedElement = (
    <UiTabsMenu
      tabs={listedIndexedTabs}
      selectedIndex={selectedTabIndex}
      onSelect={(index) => handleTabChange(null, index)}
      triggerClassName='ui-tabs-select-trigger'
      matchTriggerWidth
      dataTestId={props?.['data-testid']}
      testIdPrefix='select'
    >
      <span className='ui-tabs-select-value'>
        {selectedTab?.icon && (
          <UiIcon className='icon' icon={selectedTab.icon} size={20} />
        )}
        <span className='select-label'>{selectedTab?.title}</span>
        {selectedTab?.subTitle &&
          (selectedTab.loading ? (
            <UiSkelton variant='rectangular' height={20} width={25} />
          ) : (
            <span className='sub-title'>{selectedTab.subTitle}</span>
          ))}
      </span>
    </UiTabsMenu>
  );

  let headerElement = tabsElement;
  if (isCollapsed) {
    headerElement = collapsedElement;
  } else if (hasOverflow) {
    headerElement = (
      <div className='ui-tabs-tabs-row'>
        {tabsElement}
        {/* Empty once its only tab is the selected one, promoted into the bar */}
        {overflowIndexedTabs.length > 0 && (
          <UiTabsMenu
            tabs={overflowIndexedTabs}
            onSelect={(index) => handleTabChange(null, index)}
            triggerClassName='ui-tabs-overflow-trigger'
            dataTestId={props?.['data-testid']}
            testIdPrefix='overflow'
          >
            <span className='overflow-label'>
              {moreTabsLabel || t('common:more')}
            </span>
            <span className='overflow-badge'>
              +{overflowIndexedTabs.length}
            </span>
          </UiTabsMenu>
        )}
      </div>
    );
  }

  return (
    <StyledUiTabs className='ui-tabs'>
      <div
        className={classNames('ui-tabs-header', className, {
          'is-collapsed': isCollapsed,
        })}
      >
        {headerElement}
        {actions && <div className='ui-tabs-actions'>{actions}</div>}
      </div>
      <div className={`ui-tabs-content ${contentClassName}`}>
        {permissionedTabs[selectedTabIndex]?.content}
      </div>
    </StyledUiTabs>
  );
}

export default memo(UiTabs);

UiTabs.propTypes = {
  tabs: PropTypes.arrayOf(Object),
  defaultSelectedTabKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onChange: PropTypes.func,
  actions: PropTypes.node,
  paramKey: PropTypes.string,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  disableUrlSync: PropTypes.bool,
  /**
   * Optional. Maximum number of tabs rendered directly in the bar; the rest
   * move into the overflow menu, keeping their current order. 0 puts every
   * tab in the menu. When omitted, the component behaves exactly as before,
   * with no overflow.
   */
  maxVisibleTabs: PropTypes.number,
  /** Optional. Overflow button label. Defaults to the common:more translation. */
  moreTabsLabel: PropTypes.string,
  /**
   * Optional. Below the `sm` breakpoint, replace the tab bar with a single
   * select listing every tab. Off by default, so other pages are unaffected.
   */
  collapseOnMobile: PropTypes.bool,
  /**
   * Optional. Render the bar as non-scrollable and drop the scroll buttons,
   * but only while maxVisibleTabs is actually producing an overflow menu —
   * otherwise the bar falls back to its normal scrollable behavior so tabs
   * that don't fit the screen stay reachable.
   */
  disableScroll: PropTypes.bool,
  'data-testid': PropTypes.string,
};
